import { unmaskPhoneNumber } from '../../../services/masks';
import { isCnpj, isCpf, onlyDigits } from '../../../shared/documents';
import { isValidEmail } from '../../../utils/emailValidator';

import { ActivationFormState, ActivationStep, CollaboratorActivationFormState } from '../types';
import {
  normalizeBankHolderDocument,
  normalizeRecipientDocument
} from './activationFormHelpers';

export type ValidateActivationStepOptions = {
  mode?: 'estate' | 'collaborator';
  requireEstate?: boolean;
};

export const validateActivationStep = (
  step: ActivationStep,
  form: ActivationFormState | CollaboratorActivationFormState,
  options: ValidateActivationStepOptions = {}
): string | null => {
  const { mode = 'estate', requireEstate = false } = options;
  const isCorporation = form.recipientType === 'corporation';
  const documentNormalized = normalizeRecipientDocument(form.document, form.recipientType);
  const collaboratorForm = form as CollaboratorActivationFormState;

  if (step === 1) {
    if (!form.recipientType) return 'Selecione o tipo de cadastro.';
    if (mode === 'collaborator' && requireEstate && !collaboratorForm.estateId?.trim()) {
      return 'Selecione uma imobiliária.';
    }
    return null;
  }

  if (step === 2) {
    if (isCorporation) return null;
    if (!isValidEmail(form.email)) return 'Informe um e-mail válido.';
    if (!isCpf(documentNormalized)) {
      return 'Documento inválido para o tipo de recebedor.';
    }
    if (
      !form.name.trim() ||
      !form.birthdate ||
      !form.monthlyIncome ||
      !form.professionalOccupation.trim()
    ) {
      return 'Preencha os dados obrigatórios do recebedor.';
    }
    if (!form.phone || unmaskPhoneNumber(form.phone).length < 10) {
      return 'Informe um telefone válido.';
    }
    return null;
  }

  if (step === 3 && isCorporation) {
    if (!isValidEmail(form.email)) return 'Informe um e-mail válido da empresa.';
    if (!isCnpj(documentNormalized)) return 'Informe um CNPJ válido da empresa.';
    if (!form.phone || unmaskPhoneNumber(form.phone).length < 10) {
      return 'Informe um telefone válido da empresa.';
    }
    if (
      !form.companyName.trim() ||
      !form.tradingName.trim() ||
      !form.annualRevenue.trim()
    ) {
      return 'Preencha os dados obrigatórios da empresa.';
    }
    if (
      !form.partnerName.trim() ||
      !isValidEmail(form.partnerEmail) ||
      onlyDigits(form.partnerDocument).length !== 11 ||
      !form.partnerPhone ||
      unmaskPhoneNumber(form.partnerPhone).length < 10 ||
      !form.partnerBirthdate ||
      !form.partnerMonthlyIncome ||
      !form.partnerProfessionalOccupation.trim()
    ) {
      return 'Preencha os dados obrigatórios do responsável legal.';
    }
  }

  if (step === 4) {
    if (
      !form.street.trim() ||
      !form.streetNumber.trim() ||
      !form.neighborhood.trim() ||
      !form.city.trim() ||
      form.state.trim().length !== 2 ||
      onlyDigits(form.zipCode).length !== 8 ||
      !form.referencePoint.trim()
    ) {
      return 'Preencha corretamente o endereço principal.';
    }
    return null;
  }

  if (step === 5) {
    if (
      !form.bankHolderName.trim() ||
      !form.bankHolderDocument.trim() ||
      !form.bankCode.trim() ||
      !form.bankBranchNumber.trim() ||
      !form.bankAccountNumber.trim() ||
      !form.bankAccountCheckDigit.trim()
    ) {
      return 'Preencha todos os dados bancários obrigatórios.';
    }
    const holderDocument = normalizeBankHolderDocument(
      form.bankHolderDocument,
      form.recipientType,
      form.bankHolderType
    );
    if (holderDocument !== documentNormalized) {
      return 'Documento do titular da conta deve ser igual ao documento do recebedor.';
    }
  }

  return null;
};

export const getLastActivationStep = (): ActivationStep => 5;
